import React from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/PageHero';
import { PlatformFlow } from '../components/PlatformFlow';
import { CTASection } from '../components/CTASection';
export function Platform() {
  const capabilities = [{
    title: 'PASSENGER FLOW AND DWELL ANALYTICS',
    description: 'Boarding, alighting and stop-level dwell patterns'
  }, {
    title: 'OPERATIONAL SIGNALS',
    description: 'Occupancy trends and route or stop performance summaries'
  }, {
    title: 'SAFETY INSIGHTS',
    description: 'Behavioural patterns and high-risk location indicators'
  }, {
    title: 'GOVERNANCE AND REPORTING',
    description: 'Auditable, anonymised outputs aligned with policy and compliance requirements'
  }];
  return <>
      <PageHero eyebrow="PLATFORM" headline="An intelligence layer designed for real-world transport systems" data-id="element-265" />

      {/* Overview */}
      <section className="bg-sure-black w-full py-24 md:py-32" data-id="element-266">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12" data-id="element-267">
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="text-sure-gray text-lg md:text-xl font-light leading-relaxed max-w-3xl" data-id="element-268">
            SURE AI operates as an intelligence layer between onboard systems
            such as CCTV and telematics and existing operational environments.
            It processes signals at the edge and in the cloud to deliver
            anonymised, standardised outputs that integrate with control rooms,
            reporting frameworks and planning systems.
          </motion.p>
        </div>
      </section>

      {/* Platform Visuals Showcase */}
      <section className="bg-sure-black w-full py-16 md:py-28 overflow-hidden relative" data-id="element-269">
        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] bg-sure-accent/8 blur-[100px] rounded-full pointer-events-none" data-id="element-270" />

        <div className="max-w-[1400px] w-full mx-auto px-6 md:px-12 relative z-10" data-id="element-271">
          {/* Stacked overlapping screenshots */}
          <div className="relative w-full" style={{
          perspective: '1200px'
        }} data-id="element-272">
            {/* Screenshot 1 — KPI Dashboard (back, slightly right) */}
            <motion.div initial={{
            opacity: 0,
            y: 60
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 1.2,
            ease: 'easeOut'
          }} className="relative w-[95%] md:w-[85%] ml-auto rounded-lg overflow-hidden border border-white/10 shadow-[0_20px_80px_rgba(0,0,0,0.7)]" style={{
            transform: 'rotateY(-3deg) rotateX(2deg)'
          }} data-id="element-273">
              <img src="https://cdn.magicpatterns.com/uploads/twDh3GmcxHNxCoyVLyu5di/Screenshot_2026-04-01_120658.png" alt="KPI Dashboard" className="w-full h-auto block" data-id="element-274" />
              {/* Subtle dark tint overlay */}
              <div className="absolute inset-0 bg-sure-black/30 pointer-events-none" data-id="element-275" />
              {/* Accent color tint */}
              <div className="absolute inset-0 bg-sure-accent/10 mix-blend-overlay pointer-events-none" data-id="element-276" />
              {/* Bottom fade */}
              <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-sure-black to-transparent pointer-events-none" data-id="element-277" />
            </motion.div>

            {/* Screenshot 2 — Map View (front, overlapping from left) */}
            <motion.div initial={{
            opacity: 0,
            y: 60
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 1.2,
            delay: 0.25,
            ease: 'easeOut'
          }} className="relative w-[95%] md:w-[85%] mr-auto -mt-16 md:-mt-32 rounded-lg overflow-hidden border border-white/10 shadow-[0_20px_80px_rgba(0,0,0,0.7)]" style={{
            transform: 'rotateY(3deg) rotateX(2deg)'
          }} data-id="element-278">
              <img src="https://cdn.magicpatterns.com/uploads/u3aDykWcpDWeFtAUntMVTc/Screenshot_2026-04-01_120546.png" alt="Passenger Counting Map View" className="w-full h-auto block" data-id="element-279" />
              {/* Subtle dark tint overlay */}
              <div className="absolute inset-0 bg-sure-black/25 pointer-events-none" data-id="element-280" />
              {/* Accent color tint */}
              <div className="absolute inset-0 bg-sure-accent/10 mix-blend-overlay pointer-events-none" data-id="element-281" />
              {/* Bottom fade */}
              <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-sure-black to-transparent pointer-events-none" data-id="element-282" />
            </motion.div>
          </div>
        </div>

        {/* Top/bottom edge fades for seamless blending */}
        <div className="absolute top-0 inset-x-0 h-24 bg-gradient-to-b from-sure-black to-transparent z-20 pointer-events-none" data-id="element-283" />
        <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-sure-black to-transparent z-20 pointer-events-none" data-id="element-284" />
      </section>

      <PlatformFlow data-id="element-285" />

      {/* Closing Statement */}
      <section className="bg-sure-black w-full py-24 md:py-32 border-y border-white/5" data-id="element-286">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 text-center" data-id="element-287">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="text-sure-white text-2xl md:text-3xl lg:text-4xl font-light italic leading-relaxed max-w-4xl mx-auto" data-id="element-288">
            "Activates existing infrastructure into a continuous, decision-grade
            intelligence capability"
          </motion.h2>
        </div>
      </section>

      {/* Capabilities */}
      <section className="bg-sure-dark w-full py-24 md:py-32" data-id="element-289">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12" data-id="element-290">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="text-sure-accent text-xs md:text-sm uppercase tracking-widest font-medium mb-16" data-id="element-291">
            Capabilities
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-292">
            {capabilities.map((capability, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: index * 0.1
          }} className="bg-[#1a1a1a] border-l-2 border-sure-accent p-8" data-id="element-293">
                <h3 className="text-sure-white text-base font-bold uppercase tracking-widest mb-4" data-id="element-294">
                  {capability.title}
                </h3>
                <p className="text-sure-gray text-sm font-light leading-relaxed" data-id="element-295">
                  {capability.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Integration Note */}
      <section className="bg-sure-black w-full py-24 md:py-32" data-id="element-296">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12" data-id="element-297">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="max-w-3xl" data-id="element-298">
            <h2 className="text-sure-accent text-xs md:text-sm uppercase tracking-widest font-medium mb-6" data-id="element-299">
              Integration
            </h2>
            <p className="text-sure-gray text-base md:text-lg font-light leading-relaxed" data-id="element-300">
              Vendor-agnostic and designed to deploy alongside existing CCTV and
              telematics without replacing current systems. Phased rollout
              supports pilots, depot-level deployments and progressive
              network-wide coverage.
            </p>
          </motion.div>
        </div>
      </section>

      <CTASection data-id="element-301" />
    </>;
}